import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { Typography, Space, Tag, Row, Col, Card, Statistic, Table } from "antd";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Tags } from "../components/Tags";
import { stripMarkdown } from "../cardUtils";

interface DistributionData {
  group_name: string;
  metric: string;
  metric_unit: string;
  avg: number;
  median: number;
  percentile_25: number;
  percentile_75: number;
  std_dev: number;
  commentary: string;
  related_factors: string[];
  sample_size?: number;
}

// DistributionAnalysis Block Recipe
const distributionAnalysisRecipe = {
  id: "distribution-analysis-block",
  name: "Distribution Analysis Block",
  globalKey: [],
  prefix: `[
        {
          "group_name": "string",
          "metric": "string",
          "metric_unit": "string",
          "avg": 0,
          "median": 0,
          "percentile_25": 0,
          "percentile_75": 0,
          "std_dev": 0,
          "commentary": "string",
          "related_factors": ["string"]
          "sample_size": "number — optional"
        }
]`,
  format: {
    type: "json",
    fields: ["group_name", "metric", "avg", "median", "commentary"],
    constraints: [
      { field: "group_name", type: "string", required: true },
      { field: "metric", type: "string", required: true },
      { field: "avg", type: "number", required: true },
      { field: "median", type: "number", required: true },
      { field: "commentary", type: "string", required: true },
    ],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as DistributionData[];
          return (
            Array.isArray(parsed) &&
            parsed.every(
              (item) =>
                item.group_name &&
                item.metric &&
                typeof item.avg === "number" &&
                typeof item.median === "number" &&
                item.commentary
            )
          );
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// DistributionAnalysis Block Parser
function distributionAnalysisParser(data: any) {
  try {
    return {
      data: data[distributionAnalysisBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse distribution analysis data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// DistributionAnalysis Block Variants
const distributionAnalysisVariants = [
  {
    id: "default",
    name: "Default",
    description:
      "Full details: group, metric, avg, median, percentiles, std dev, commentary, related factors, sample size",
    defaultSettings: {},
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Group, metric, avg, std dev, short summary",
    defaultSettings: {},
  },
  {
    id: "modern",
    name: "Modern",
    description: "Emphasize key stats visually, compact layout",
    defaultSettings: {},
  },
  {
    id: "table",
    name: "Table",
    description: "Tabular view for quick comparison",
    defaultSettings: {},
  },
];

// DistributionAnalysis Block Renderer
function DistributionAnalysisBlockRenderer(props: any) {
  const { data, variant, className, style } = props;
  if (data === undefined) {
    return null;
  }
  const distributionsData = data[distributionAnalysisBlock.id];
  const variantSettings = distributionAnalysisVariants.find(
    (v) => v.id === variant
  )?.defaultSettings;

  if (!distributionsData || distributionsData.length === 0) return null;

  // Variant renderers
  const renderDefaultCard = (item: DistributionData) => (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          justifyContent: "space-between",
        }}
      >
        <Typography.Text className="vzbl-header-5">
          {stripMarkdown(item.group_name)}
        </Typography.Text>
        {item.sample_size !== undefined && (
          <Tag className="vzbl-pill-tag">{`Sample Size: ${item.sample_size}`}</Tag>
        )}
      </div>
      <Typography.Text type="secondary">
        {item.metric} {item.metric_unit}
      </Typography.Text>
      <Typography.Text strong>Avg: {item.avg}</Typography.Text>
      <Typography.Text>Median: {item.median}</Typography.Text>
      <Typography.Text>
        25th: {item.percentile_25} | 75th: {item.percentile_75}
      </Typography.Text>
      <Typography.Text>Std Dev: {item.std_dev}</Typography.Text>

      {item.commentary && (
        <Markdown
          remarkPlugins={[remarkGfm]}
          skipHtml={true}
          className="vzbl-text-3"
        >
          {item.commentary}
        </Markdown>
      )}
      {item.related_factors?.length > 0 && <Tags tags={item.related_factors} />}
    </Space>
  );

  const renderMinimalCard = (item: DistributionData) => (
    <Space direction="vertical" size={2} style={{ width: "100%" }}>
      <Typography.Text className="vzbl-header-5">
        {stripMarkdown(item.group_name)}
      </Typography.Text>
      <Typography.Text strong>
        Avg: {item.avg} {item.metric_unit}
      </Typography.Text>
      <Typography.Text type="secondary">
        Std Dev: {item.std_dev}
      </Typography.Text>
      {item.commentary && (
        <Markdown
          remarkPlugins={[remarkGfm]}
          skipHtml={true}
          className="vzbl-text-3"
        >
          {item.commentary}
        </Markdown>
      )}
    </Space>
  );

  const renderModernCard = (item: DistributionData) => (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Typography.Text className="vzbl-header-5" style={{ fontWeight: 700 }}>
          {stripMarkdown(item.group_name)}
        </Typography.Text>
        <Tags tags={[item.metric]} />
      </div>
      <Typography.Text className="vzbl-text-3" style={{ margin: 0 }}>
        Avg: {item.avg} {item.metric_unit}
      </Typography.Text>
      <Typography.Text className="vzbl-text-3">
        Std Dev: {item.std_dev}
      </Typography.Text>
      <Typography.Text className="vzbl-text-3">
        Median: {item.median}
      </Typography.Text>
      {item.commentary && (
        <Markdown
          remarkPlugins={[remarkGfm]}
          skipHtml={true}
          className="vzbl-text-3"
        >
          {item.commentary}
        </Markdown>
      )}
    </Space>
  );

  const renderTable = (data: DistributionData[]) => {
    const columns = [
      { title: "Group", dataIndex: "group_name", key: "group_name" },
      { title: "Metric", dataIndex: "metric", key: "metric" },
      { title: "Avg", dataIndex: "avg", key: "avg" },
      { title: "Median", dataIndex: "median", key: "median" },
      { title: "25th", dataIndex: "percentile_25", key: "percentile_25" },
      { title: "75th", dataIndex: "percentile_75", key: "percentile_75" },
      { title: "Std Dev", dataIndex: "std_dev", key: "std_dev" },
      { title: "Unit", dataIndex: "metric_unit", key: "metric_unit" },
      { title: "Sample Size", dataIndex: "sample_size", key: "sample_size" },
    ];
    return (
      <Table
        columns={columns}
        dataSource={data}
        pagination={false}
        rowKey={(r) => r.group_name + r.metric}
        size="small"
      />
    );
  };

  // Main renderer for all variants
  const getCardRenderer = () => {
    switch (variant) {
      case "minimal": {
        return renderMinimalCard;
      }
      case "modern": {
        return renderModernCard;
      }
      default: {
        return renderDefaultCard;
      }
    }
  };

  if (variant === "table") {
    return (
      <div className="vzbl-panel-container">
        <Typography.Title level={4} className="vzbl-header-4">
          Distribution Analysis
        </Typography.Title>
        {renderTable(distributionsData)}
      </div>
    );
  }
  const renderCard = getCardRenderer();
  return (
    <div className="vzbl-panel-container">
      <Typography.Title level={4} className="vzbl-header-4">
        Distribution Analysis
      </Typography.Title>
      <div className="responsive-card-grid">
        {distributionsData.map((item: DistributionData, idx: number) => (
          <div
            className="vzbl-panel-shadow vzbl-panel-overlay"
            key={`distribution-${idx}`}
          >
            {renderCard(item)}
          </div>
        ))}
      </div>
    </div>
  );
}

// Create DistributionAnalysis Block
const distributionAnalysisBlock = createBaseBlock({
  id: "distribution-analysis",
  name: "Distribution Analysis Block",
  description: "Displays distribution analysis with statistical metrics",
  recipe: distributionAnalysisRecipe,
  variants: distributionAnalysisVariants,
  parser: distributionAnalysisParser,
  renderer: DistributionAnalysisBlockRenderer,
});

// Export the block for direct use
export { distributionAnalysisBlock };
